import { invert } from "lodash";
import { GameFragment } from "../graphql/game";

export type ParsedFen = ReadonlyArray<ReadonlyArray<string | null>>;
export type Color = "black" | "white";
export type Army =
  | "classic"
  | "nemesis"
  | "empowered"
  | "reaper"
  | "two_kings"
  | "animals";
export type Coordinate = [number, number];

// Convert [0, 6] to "a2".
const encodeCoordinate = (c: Coordinate): string =>
  String.fromCharCode(c[0] + 97) + (8 - c[1]).toString();

// Convert "a2" to [0, 6].
const decodeCoordinate = (s: string): Coordinate => [
  s.charCodeAt(0) - 97,
  8 - parseInt(s.slice(1, 2), 10),
];

// Are two Coordinates the same value?
export const coordinatesEqual = (x: Coordinate, y: Coordinate) =>
  x[0] === y[0] && x[1] === y[1];

export const coordinatesFromUci = (uci: string): [Coordinate, Coordinate] => [
  decodeCoordinate(uci.slice(0, 2)),
  decodeCoordinate(uci.slice(2, 4)),
];

export const encodeMove = (from: Coordinate, to: Coordinate): string =>
  encodeCoordinate(from) + encodeCoordinate(to);

export const countChallenges = (uci: string) =>
  uci.split("").reduce((acc, chr) => acc + (chr === ":" ? 1 : 0), 0);

const parseFen = (fen: string): ParsedFen =>
  fen.split("/").map((row) =>
    row.split("").reduce((acc: string[], piece: string) => {
      if (piece.charCodeAt(0) >= 49 && piece.charCodeAt(0) <= 56) {
        return acc.concat(new Array(parseInt(piece, 10)).fill(null));
      } else {
        return acc.concat([piece]);
      }
    }, [])
  );

const EPD_COLOR: { [code: string]: Color } = {
  w: "white",
  b: "black",
  K: "white",
  k: "black",
};
const EPD_ARMIES: { [K in string]: Army } = {
  c: "classic",
  n: "nemesis",
  e: "empowered",
  r: "reaper",
  k: "two_kings",
  a: "animals",
};
export const ARMIES: Array<Army> = Object.values(EPD_ARMIES) as any;
const ARMIES_EPD = invert(EPD_ARMIES);
export const OTHER_COLOR: { [key: string]: Color } = {
  white: "black",
  black: "white",
};
export const PIECE_NAME = {
  k: "King",
  q: "Queen",
  r: "Rook",
  b: "Bishop",
  n: "Knight",
  p: "Pawn",
};
export const PIECE_UNICODE: { [code: string]: string } = {
  k: "♔",
  q: "♕",
  r: "♖",
  b: "♗",
  n: "♘",
  p: "♙",
};
export const COLOR_ORDER = { white: 0, black: 1 };

export default class GameState implements GameFragment {
  // Transform a GraphQL Game into a GameState
  static fromGame(game: any): GameState {
    const ret = new GameState();
    ret.updateFromServer(game);
    return ret;
  }

  static fromEpd(epd: string): GameState {
    const ret = new GameState();
    ret.parseEpd(epd);
    return ret;
  }

  static fromArmies(white: Army, black: Army): GameState {
    let fen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR";
    if (white == "two_kings") fen = fen.replace("Q", "K");
    if (black == "two_kings") fen = fen.replace("q", "k");
    const epd = `${fen} w KQkq - 0 1 ${ARMIES_EPD[white]}${ARMIES_EPD[black]} 33`;
    return GameState.fromEpd(epd);
  }

  static empty: GameState;

  id!: string;
  white!: {
    id: string;
    name: string;
  };
  black!: {
    id: string;
    name: string;
  };
  createdAt!: string;
  updatedAt!: string;
  epd!: string;
  legalMoves!: string[] | null;
  lastEpd!: string | null;
  lastMove!: string | null;
  availableDuels!: string[] | null;
  toAct!: string | null;
  nextAction?: "move" | "offer_duel" | "resolve_duel";
  pendingDuelBid?: number;
  winner!: string | null;
  pieces!: ParsedFen;
  toMove!: Color;
  kingTurn!: boolean;
  armies!: [Army, Army];
  stones!: [number, number];
  lastMoveCoords!: [Coordinate, Coordinate];

  parseEpd(epd: string) {
    const [
      fen,
      toMove,
      /* eslint-disable no-unused-vars */
      _castleRights,
      _epSquare,
      _hmClock,
      _moveNumber,
      /* eslint-enable no-unused-vars */
      armies,
      stones,
    ] = epd.split(" ");
    this.epd = epd;
    this.pieces = parseFen(fen);
    this.toMove = EPD_COLOR[toMove as keyof typeof EPD_COLOR];
    this.kingTurn = toMove.toLowerCase() === "k";
    this.armies = armies.split("").map((a) => EPD_ARMIES[a]) as any;
    this.stones = stones.split("").map((s) => parseInt(s, 10)) as any;
  }

  // Process an update from the server
  updateFromServer(data: Partial<GameState>) {
    this.parseEpd(data.epd!);
    this.legalMoves = data.legalMoves!;
    this.availableDuels = data.availableDuels || [];
    this.winner = data.winner!;
    this.lastEpd = data.lastEpd!;
    this.lastMove = data.lastMove!;
    if (data.lastMove) {
      this.lastMoveCoords = coordinatesFromUci(data.lastMove);
    }
  }

  lastMoveColor(): Color {
    return this.kingTurn ? this.toMove : OTHER_COLOR[this.toMove];
  }

  canChallenge(): boolean {
    return !!(this.availableDuels && this.availableDuels.length > 1);
  }

  availableChallenges(): Array<number> {
    const lastMove = this.lastMove;
    if (!lastMove) return [];
    return Object.keys(
      this.availableDuels!.reduce((acc: { [key: string]: number }, d) => {
        const option = d.substr(lastMove.length + 1, 1);
        if (option.length > 0 && option[0] !== ":") {
          acc[option] = 1;
        }
        return acc;
      }, {})
    ).map((k) => parseInt(k, 10));
  }

  availableResponses(): Array<number> {
    const lastMove = this.lastMove;
    if (!lastMove) return [];
    return Object.keys(
      this.availableDuels!.reduce((acc: { [key: string]: number }, d) => {
        const option = d.substr(lastMove.length + 2, 1);
        if (option.length > 0 && option[0] !== ":") {
          acc[option] = 1;
        }
        return acc;
      }, {})
    ).map((k) => parseInt(k, 10));
  }

  isPromotingMove(from: Coordinate, to: Coordinate): boolean {
    const piece = this.pieces[from[1]][from[0]];
    return (piece === "P" && to[1] === 0) || (piece === "p" && to[1] === 7);
  }

  applyMove(from: Coordinate, to: Coordinate): GameState {
    const ret = new GameState();
    if (this.isPromotingMove(from, to)) {
      throw new Error("Not implemented");
    }
    ret.epd = "applyMove NOT IMPLEMENTED";
    const pieces = this.pieces.slice() as (string | null)[][];
    pieces[from[1]] = pieces[from[1]].slice();
    if (to[1] !== from[1]) {
      pieces[to[1]] = pieces[to[1]].slice();
    }
    const piece = pieces[from[1]][from[0]];
    pieces[from[1]][from[0]] = null;
    pieces[to[1]][to[0]] = piece;
    ret.pieces = pieces;
    return ret;
  }

  listOccupiedSquares(
    occupiedBy: "any" | null | "white" | "black"
  ): Array<Coordinate> {
    let predicate: (space: string | null) => boolean;
    if (occupiedBy === "any") {
      predicate = (space: string | null) => !!space;
    } else if (occupiedBy === "white") {
      predicate = (space: string | null) =>
        !!space && space.toUpperCase() === space;
    } else if (occupiedBy === "black") {
      predicate = (space: string | null) =>
        !!space && space.toLowerCase() === space;
    } else {
      predicate = (space: string | null) => !space;
    }
    return this.pieces
      .map((row, y) =>
        row.reduce(
          (acc: Coordinate[], space: string | null, x): Coordinate[] => {
            if (predicate(space)) {
              return [...acc, [x, y]];
            } else {
              return acc;
            }
          },
          []
        )
      )
      .reduce((a, b) => a.concat(b));
  }

  listReachableFrom(from: Coordinate): Array<Coordinate> {
    if (!this.legalMoves) return [];
    const start = encodeCoordinate(from);
    return this.legalMoves
      .filter((m) => m.slice(0, 2) === start)
      .map((m) => decodeCoordinate(m.slice(2)));
  }
}

const empty = (GameState.empty = new GameState());
empty.pieces = parseFen("8/8/8/8/8/8/8/8");
